import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { Banner, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth/AuthContext';
import { databaseApi } from './api';

interface FormState {
  name: string;
  gender: string;
  birthPlace: string;
  birthDate: string;
  phone: string;
  email: string;
  photoKey: string | null;
  personalBranding: string;
  profession: string;
  lastEducation: string;
  status: string;
  tbmId: string;
}

const EMPTY: FormState = {
  name: '',
  gender: '',
  birthPlace: '',
  birthDate: '',
  phone: '',
  email: '',
  photoKey: null,
  personalBranding: '',
  profession: '',
  lastEducation: '',
  status: 'active',
  tbmId: '',
};

export function PelitaFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isPelita = user?.role === 'pelita';

  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const { data: tbmList } = useQuery({
    queryKey: ['tbm', 'all'],
    queryFn: () => databaseApi.listTbm({ page: 1, pageSize: 200 }),
  });

  const { data: existing, isLoading } = useQuery({
    queryKey: ['individual', id],
    queryFn: () => databaseApi.getIndividual(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    setForm((f) => ({
      ...f,
      name: existing.name,
      gender: existing.gender ?? '',
      birthPlace: existing.birthPlace ?? '',
      birthDate: existing.birthDate ?? '',
      phone: existing.phone ?? '',
      email: existing.email ?? '',
      photoKey: existing.photoKey ?? null,
      personalBranding: existing.personalBranding ?? '',
      profession: existing.profession ?? '',
      lastEducation: existing.lastEducation ?? '',
      status: existing.status,
      tbmId: existing.tbmId ?? '',
    }));
  }, [existing]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => ({ ...e, [key]: '' }));
  };

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      isEdit ? databaseApi.updateIndividual(id!, payload) : databaseApi.createIndividual(payload),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['individuals'] });
      navigate(`/database/pelita/${isEdit ? id : (saved as { id: string }).id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setFieldErrors(err.fields ?? {});
        setBanner(err.fields ? null : err.message);
      } else {
        setBanner('Terjadi kesalahan. Silakan coba lagi.');
      }
    },
  });

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setBanner('Ukuran berkas foto maksimal 5 MB.');
      return;
    }

    setUploadingAvatar(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('entityType', 'pelita_avatar');
      body.append('entityId', id ?? 'new');

      const res = await api.post<{ id: string }>('/files', body);
      set('photoKey', res.id);
      setBanner(null);
    } catch (err) {
      setBanner(err instanceof ApiError ? err.message : 'Gagal mengunggah foto profil.');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setBanner(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Nama wajib diisi';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setBanner('Beberapa isian wajib belum lengkap.');
      return;
    }

    // `code` tidak dikirim — server yang menerbitkannya.
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      gender: form.gender || null,
      birthPlace: form.birthPlace || null,
      birthDate: form.birthDate || null,
      phone: form.phone || null,
      email: form.email || null,
      photoKey: form.photoKey || null,
      personalBranding: form.personalBranding.trim() || null,
      profession: form.profession.trim() || null,
      lastEducation: form.lastEducation || null,
    };

    if (!isPelita) {
      payload.status = form.status;
      payload.tbmId = form.tbmId || null;
    }

    mutation.mutate(payload);
  }

  if (isEdit && isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    // noValidate — alasan sama seperti di TbmFormPage: validasi kita sendiri
    // yang menampilkan banner dan pesan per-field, bukan bubble bawaan browser.
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <Link
        to={isEdit ? `/database/pelita/${id}` : '/database/pelita'}
        className="text-sm text-ink-secondary hover:text-primary"
      >
        ← Batal
      </Link>

      <PageHeader title={isEdit ? 'Ubah Profil PELITA SLI' : 'PELITA SLI Baru'} />

      {banner && <Banner tone="danger">{banner}</Banner>}

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Identitas</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nama lengkap"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            error={fieldErrors.name}
            required
          />
          <Input
            label="Kode PELITA SLI"
            value={isEdit ? (existing?.code ?? '') : 'dibuat otomatis setelah disimpan'}
            readOnly
            disabled
            hint="Nomor urut diterbitkan sistem dan tidak bisa diubah."
          />
          <Input
            label="Personal Branding"
            value={form.personalBranding}
            onChange={(e) => set('personalBranding', e.target.value)}
            placeholder="Misal: Pegiat Literasi Anak & Pendongeng"
            hint="Keahlian khusus atau catatan peran utama"
          />
          <Input
            label="Profesi"
            value={form.profession}
            onChange={(e) => set('profession', e.target.value)}
            placeholder="Misal: Guru, Wiraswasta, Mahasiswa"
          />
          <Select
            label="Pendidikan Terakhir"
            value={form.lastEducation}
            onChange={(e) => set('lastEducation', e.target.value)}
          >
            <option value="">— pilih pendidikan —</option>
            <option value="SMA">SMA / SMK / MA</option>
            <option value="D3">Diploma (D3)</option>
            <option value="S1">Sarjana (S1)</option>
            <option value="S2">Magister (S2)</option>
          </Select>
          <Select
            label="Jenis kelamin"
            value={form.gender}
            onChange={(e) => set('gender', e.target.value)}
          >
            <option value="">— tidak diisi —</option>
            <option value="L">Laki-laki</option>
            <option value="P">Perempuan</option>
          </Select>
          <Input
            label="Tempat lahir"
            value={form.birthPlace}
            onChange={(e) => set('birthPlace', e.target.value)}
          />
          <Input
            label="Tanggal lahir"
            type="date"
            value={form.birthDate}
            onChange={(e) => set('birthDate', e.target.value)}
            error={fieldErrors.birthDate}
          />
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Foto Profil / Avatar</h2>
        <div className="flex flex-wrap items-center gap-4">
          {form.photoKey ? (
            <img
              src={`/api/v1/files/${form.photoKey}`}
              alt="Foto Profil"
              className="size-20 rounded-full object-cover border border-border shadow-sm"
            />
          ) : (
            <div className="grid size-20 place-items-center rounded-full bg-app text-2xl border border-dashed border-border text-ink-muted">
              👤
            </div>
          )}
          <div>
            <label className="inline-flex cursor-pointer items-center justify-center rounded-[var(--radius-button)] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-colors">
              <span>{uploadingAvatar ? 'Mengunggah…' : form.photoKey ? 'Ganti Foto' : 'Unggah Foto Profil'}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
                disabled={uploadingAvatar}
              />
            </label>
            {form.photoKey && (
              <button
                type="button"
                onClick={() => set('photoKey', null)}
                className="ml-3 text-sm text-danger hover:underline cursor-pointer"
              >
                Hapus Foto
              </button>
            )}
            <p className="mt-1.5 text-xs text-ink-muted">
              Format: JPG, PNG, atau WebP · Maksimal 5 MB
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Kontak</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="No. HP"
            type="tel"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            error={fieldErrors.email}
          />
        </div>
        <p className="mt-4 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-[13px] text-ink-secondary">
          Isi kontak seperlunya saja. Data pribadi hanya terlihat oleh Super Admin dan assessor yang
          mendampingi TBM bersangkutan.
        </p>
      </Card>

      {!isPelita && (
        <>
          <Card>
            <h2 className="mb-4 text-[17px] font-semibold">Keanggotaan</h2>
            <div className="sm:max-w-md">
              <Select
                label="TBM"
                value={form.tbmId}
                onChange={(e) => set('tbmId', e.target.value)}
                error={fieldErrors.tbmId}
                hint="Boleh dikosongkan sementara — isi begitu TBM-nya sudah pasti."
              >
                <option value="">Pilih TBM</option>
                {(tbmList?.data ?? []).map((t) => {
                  const loc = [t.regionName, t.cityName, t.provinceName].filter(Boolean).join(', ');
                  return (
                    <option key={t.id} value={t.id}>
                      {t.name}{loc ? ` · ${loc}` : ''}
                    </option>
                  );
                })}
              </Select>
            </div>
          </Card>

          <Card>
            <h2 className="mb-4 text-[17px] font-semibold">Status</h2>
            <div className="sm:max-w-xs">
              <Select
                label="Status"
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
              >
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
                <option value="alumni">Alumni</option>
              </Select>
            </div>
          </Card>
        </>
      )}

      <div className="flex flex-wrap gap-3">
        <Button type="submit" loading={mutation.isPending}>
          {isEdit ? 'Simpan Perubahan' : 'Simpan PELITA SLI'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(isEdit ? `/database/pelita/${id}` : '/database/pelita')}
        >
          Batal
        </Button>
      </div>
    </form>
  );
}
